// ─────────────────────────────────────────────────────────────
// purchaseOrder_EntryGst.jsx — Add / View / Edit
// ─────────────────────────────────────────────────────────────

import React, {
  useState,
  useEffect,
  useCallback,
  useContext,
  useMemo,
} from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import LayoutWrapper from "../../components/LayoutWrapper";
import HeaderBar from "../../components/HeaderBar";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";
import {
  FileText,
  Save,
  ArrowLeft,
  Package,
  Calculator,
  Building2,
} from "lucide-react";

const API = "https://nissi-india.com/api";

const todayISO = () => new Date().toISOString().slice(0, 10);
const num = (v, d = 0) => {
  const n = parseFloat(v);
  return isNaN(n) ? d : n;
};

const toISODate = (v) => {
  if (!v) return "";
  const s = String(v);
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(s);
  if (m) {
    return `${m[3]}-${m[2].padStart(2, "0")}-${m[1].padStart(2, "0")}`;
  }
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
};

const PurchaseOrderEntryGst = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const storedBranchIdRaw =
    localStorage.getItem("branchId") || localStorage.getItem("branchid") || "";
  const storedBranch = localStorage.getItem("branch") || "";
  const userId = localStorage.getItem("userId") || "";

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": storedBranchIdRaw,
      "x-branch": storedBranch,
      "x-userid": userId,
    }),
    [finYearId, finYear, storedBranchIdRaw, storedBranch, userId]
  );

  /* ── Mode ───────────────────────────────────────────────── */
  const purchaseOrderId =
    location.state?.purchaseOrderId ||
    searchParams.get("poid") ||
    searchParams.get("id") ||
    null;

  const mode =
    location.state?.mode ||
    searchParams.get("mode") ||
    (purchaseOrderId ? "view" : "add");

  const isView = mode === "view";
  const isEdit = mode === "edit";
  const isAdd = !isView && !isEdit;
  const locked = isView;

  /* ── Styles ─────────────────────────────────────────────── */
  const inputClass = `w-full px-3 py-2.5 rounded-lg border text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-${baseColor}-500/30 focus:border-${baseColor}-500 ${
    isDark
      ? "bg-slate-800/60 border-slate-600/50 text-white placeholder-slate-400"
      : "bg-white border-slate-300 text-slate-900 placeholder-slate-400"
  }`;

  const roInputClass = `w-full px-3 py-2.5 rounded-lg border text-sm ${
    isDark
      ? "bg-slate-800/40 border-slate-600/50 text-slate-300"
      : "bg-gray-50 border-slate-200 text-slate-700"
  }`;

  const fieldClass = locked ? roInputClass : inputClass;
  const numInputClass = `${fieldClass} text-right`;

  const labelClass = `block text-xs font-semibold tracking-wide mb-1.5 ${
    isDark ? "text-slate-300" : "text-slate-700"
  }`;

  /* ── Nav state (Add flow) ───────────────────────────────── */
  const selectedItems = location.state?.selectedItems || [];
  const supplierIdFromState = location.state?.supplierId || "";
  const branchId =
    Number(location.state?.branchId) || Number(storedBranchIdRaw) || 0;
  const branchtype_session = location.state?.branchtype_session || "BRANCH";

  const [supplierId, setSupplierId] = useState(supplierIdFromState);
  const [supplierName, setSupplierName] = useState(
    location.state?.supplierName || ""
  );
  const [supplierAddress, setSupplierAddress] = useState(
    location.state?.supplierAddress || ""
  );

  const [project, setProject] = useState({ id: "", name: "", empList: [] });
  const [items, setItems] = useState([]);
  const [poNo, setPoNo] = useState("");
  const [poId, setPoId] = useState(purchaseOrderId || null);
  const [pageLoading, setPageLoading] = useState(false);

  const [totals, setTotals] = useState({
    subtotal: 0,
    ds: 0,
    adisTotal: 0,
    sgst: 0,
    cgst: 0,
    igst: 0,
    roundOff: 0,
    totalValue: 0,
  });

  const [freight, setFreight] = useState(0);
  const [freightTax, setFreightTax] = useState(false);
  const [frtSGST, setFrtSGST] = useState(0);
  const [frtCGST, setFrtCGST] = useState(0);
  const [frtIGST, setFrtIGST] = useState(0);

  const [insurance, setInsurance] = useState(0);
  const [insuranceTax, setInsuranceTax] = useState(false);
  const [insSGST, setInsSGST] = useState(0);
  const [insCGST, setInsCGST] = useState(0);
  const [insIGST, setInsIGST] = useState(0);

  const [pf, setPf] = useState(0);
  const [pfTax, setPfTax] = useState(false);
  const [pfSGST, setPfSGST] = useState(0);
  const [pfCGST, setPfCGST] = useState(0);
  const [pfIGST, setPfIGST] = useState(0);

  const [tcsAmt, setTcsAmt] = useState(0);

  const [form, setForm] = useState({
    date: todayISO(),
    dueDate: "",
    refNo: "",
    refDate: "",
    quotationNo: "",
    quotationDate: "",
    qtnRef: "",
    deliveryMethod: "",
    deliveryPlace: "",
    payTerms: "",
    priceBasics: "",
    warranty: "",
    frtDesc: "",
    remarks: "",
    employee: "",
  });

  const [saving, setSaving] = useState(false);

  /* ── Load project / employees (Add) ─────────────────────── */
  const fetchProjectData = async (reqId, bId, supId) => {
    try {
      const res = await axios.get(`${API}/getProjectDetailsByPurchaseRequest`, {
        params: { id: reqId, branchid: bId, fld_supplierid: supId },
        headers: commonHeaders,
      });

      setProject({
        id: res.data.projectDetails?.[0]?.fld_projectid || "",
        name: res.data.projectDetails?.[0]?.fld_projectname || "",
        empList: res.data.employeeSignature || [],
      });

      if (!supplierName) {
        setSupplierName(res.data.supDetails?.[0]?.fld_suppliername || "");
      }
      if (!supplierAddress) {
        setSupplierAddress(res.data.supDetails?.[0]?.fld_address1 || "");
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to load project / employee data");
    }
  };

  /* ── Load existing PO (View / Edit) ─────────────────────── */
  const loadExistingPo = async (id) => {
    try {
      setPageLoading(true);
      const res = await axios.get(`${API}/getPurchaseOrderById`, {
        params: { id },
        headers: commonHeaders,
      });

      const d = res.data?.data || res.data;
      if (!d) {
        toast.error("Purchase order not found");
        return;
      }

      const master = d.master || d;
      const detailRows = d.items || d.details || d.rows || [];

      setPoId(
        master.fld_PurchaseOrderid ||
          master.fld_purchaseorderid ||
          master.poid ||
          id
      );
      setPoNo(
        master.fld_branchwiseno ||
          master.fld_purchaseorder_no ||
          master.purchaseorder_no ||
          ""
      );
      setSupplierId(
        String(master.fld_supplierid || master.supplierid || "")
      );
      setSupplierName(master.fld_suppliername || master.suppliername || "");
      setSupplierAddress(master.fld_address1 || master.address || "");

      setProject({
        id: master.fld_projectid || master.projectid || "",
        name: master.fld_projectname || master.projectname || "",
        empList: d.employeeSignature || d.empList || [],
      });

      setForm({
        date: toISODate(master.fld_Date || master.fld_date || master.Date),
        dueDate: toISODate(master.fld_DueDate || master.fld_duedate),
        refNo: master.fld_priorpurchaseref_no || master.priorpurchaseref_no || "",
        refDate: toISODate(master.fld_refDate || master.refDate),
        quotationNo: master.fld_quotationno || master.quotationno || "",
        quotationDate: toISODate(master.fld_quotationdate || master.quotationdate),
        qtnRef: master.fld_qtnref || master.qtnref || "",
        deliveryMethod: master.fld_deliverymethod || master.deliverymethod || "",
        deliveryPlace: master.fld_deliveryplace || master.deliveryplace || "",
        payTerms: master.fld_payterms || master.payterms || "",
        priceBasics: master.fld_pricebasics || master.pricebasics || "",
        warranty: master.fld_warrenty || master.warranty || "",
        frtDesc: master.fld_frtdescription || master.frtdescription || "",
        remarks: master.fld_remarks || master.remarks || "",
        employee: String(master.fld_employeeid || master.employeeid || ""),
      });

      setFreight(num(master.fld_frieght || master.freight));
      setFreightTax(String(master.fld_frttax || master.frttax) === "1");
      setFrtSGST(num(master.fld_frtsgst || master.frtsgst));
      setFrtCGST(num(master.fld_frtcgst || master.frtcgst));
      setFrtIGST(num(master.fld_frtigst || master.frtigst));

      setInsurance(num(master.fld_insurance || master.insurance));
      setInsuranceTax(String(master.fld_instax || master.instax) === "1");
      setInsSGST(num(master.fld_inssgst || master.inssgst));
      setInsCGST(num(master.fld_inscgst || master.inscgst));
      setInsIGST(num(master.fld_insigst || master.insigst));

      setPf(num(master.fld_pf || master.pf));
      setPfTax(String(master.fld_pftax || master.pftax) === "1");
      setPfSGST(num(master.fld_pfsgst || master.pfsgst));
      setPfCGST(num(master.fld_pfcgst || master.pfcgst));
      setPfIGST(num(master.fld_pfigst || master.pfigst));
      setTcsAmt(num(master.fld_tcsamt || master.tcsamt));

      setItems(
        detailRows.map((o) => {
          const poQty = num(o.fld_poqty || o.POQty || o.poQty, 0);
          const price = num(o.fld_ppoprice || o.ppoprice || o.price, 0);
          return {
            fld_itemid: o.fld_itemid || o.itemid,
            fld_itemcode: o.fld_itemcode || o.itemcode || "",
            fld_itemname: o.fld_itemname || o.itemname || "",
            fld_uomname: o.fld_uomname || o.uomname || "",
            fld_qty: o.fld_reqqty || o.ReqQty || o.reqQty || 0,
            fld_purchaserequestid:
              o.fld_purchaserequestid || o.PurchaseRequestid || 0,
            fld_storeid: o.fld_storeid || o.storeid || 0,
            fld_pendingqty: o.fld_pendingqty || o.PendingQty || 0,
            hsnno: o.fld_itemhsnno || o.itemhsnno || o.hsnno || "",
            specification: o.fld_specification || o.specification || "",
            poQty,
            price,
            dsPercent: num(o.fld_DS || o.DS || o.dsPercent),
            sgst: num(o.fld_VAT || o.VAT || o.sgst),
            cgst: num(o.fld_CST || o.CST || o.cgst),
            igst: num(o.fld_ED || o.ED || o.igst),
            sc: num(o.fld_SC || o.SC || o.sc),
            pandf: num(o.fld_PF || o.PF || o.pandf),
            total: poQty * price,
          };
        })
      );
    } catch (err) {
      console.error(err);
      toast.error(
        err.response?.data?.message || "Failed to load purchase order"
      );
    } finally {
      setPageLoading(false);
    }
  };

  /* ── Mount ──────────────────────────────────────────────── */
  useEffect(() => {
    if (purchaseOrderId && (isView || isEdit)) {
      loadExistingPo(purchaseOrderId);
      return;
    }

    // Add mode
    if (selectedItems.length === 0) return;

    setItems(
      selectedItems.map((o) => ({
        ...o,
        hsnno: o.fld_itemHSNNo || o.fld_hsncode || "",
        specification: o.fld_specification || "",
        poQty: num(o.POQty || o.fld_qty, 0),
        price: num(o.fld_ppoprice || o.fld_price, 0),
        dsPercent: num(o.fld_DS, 0),
        sgst: num(o.fld_VAT, 0),
        cgst: num(o.fld_CST, 0),
        igst: num(o.fld_ED, 0),
        sc: num(o.fld_SC, 0),
        pandf: num(o.fld_PandF, 0),
        total:
          num(o.POQty || o.fld_qty, 0) * num(o.fld_ppoprice || o.fld_price, 0),
      }))
    );

    fetchProjectData(
      selectedItems[0].fld_purchaserequestid,
      branchId,
      supplierIdFromState
    );

    const purNos = [
      ...new Set(
        selectedItems.map((i) => i.fld_purchaserequestno).filter(Boolean)
      ),
    ].join(", ");
    setForm((prev) => ({ ...prev, refNo: purNos }));

    const loadPoNo = async () => {
      try {
        const res = await axios.get(`${API}/getNextPoNo`, {
          params: { branchid: branchId },
          headers: commonHeaders,
        });
        const no = res.data?.poNo || res.data?.data?.poNo || "";
        if (no) setPoNo(String(no));
      } catch (_) {}
    };
    if (branchId) loadPoNo();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const setField = (field, val) =>
    setForm((prev) => ({ ...prev, [field]: val }));

  const handleItemChange = (idx, field, val) => {
    if (locked) return;
    setItems((prev) => {
      const upd = prev.map((it, i) => (i !== idx ? it : { ...it, [field]: val }));
      if (field === "poQty" || field === "price") {
        upd[idx].total = num(upd[idx].poQty, 0) * num(upd[idx].price, 0);
      }
      return upd;
    });
  };

  const recalculate = useCallback(async () => {
    if (items.length === 0) return;
    try {
      const res = await axios.post(
        `${API}/calculatePurchaseOrderDetails`,
        {
          items: items.map((it) => ({
            fld_qtyprice: it.total,
            fld_DS: it.dsPercent,
            fld_VAT: it.sgst,
            fld_CST: it.cgst,
            fld_ED: it.igst,
            fld_SC: it.sc,
            fld_PandF: it.pandf,
          })),
          freight,
          insurance,
          pf,
          tcsamt: tcsAmt,
          frtTaxes: freightTax
            ? { sgst: frtSGST, cgst: frtCGST, igst: frtIGST }
            : { sgst: 0, cgst: 0, igst: 0 },
          insTaxes: insuranceTax
            ? { sgst: insSGST, cgst: insCGST, igst: insIGST }
            : { sgst: 0, cgst: 0, igst: 0 },
          pfTaxes: pfTax
            ? { sgst: pfSGST, cgst: pfCGST, igst: pfIGST }
            : { sgst: 0, cgst: 0, igst: 0 },
        },
        { headers: commonHeaders }
      );
      const d = res.data;
      setTotals({
        subtotal: num(d.subtotal, 0),
        ds: num(d.discount, 0),
        adisTotal: num(d.subtotal, 0) - num(d.discount, 0),
        sgst: num(d.vat, 0),
        cgst: num(d.cst, 0),
        igst: num(d.ed, 0),
        roundOff: num(d.transport, 0),
        totalValue: num(d.totalvalue, 0),
      });
    } catch (err) {
      console.error(err);
    }
  }, [
    items,
    freight,
    insurance,
    pf,
    tcsAmt,
    freightTax,
    frtSGST,
    frtCGST,
    frtIGST,
    insuranceTax,
    insSGST,
    insCGST,
    insIGST,
    pfTax,
    pfSGST,
    pfCGST,
    pfIGST,
    commonHeaders,
  ]);

  useEffect(() => {
    recalculate();
  }, [recalculate]);

  const validate = () => {
    if (!form.date) return toast.error("Please select Date"), false;
    if (!form.dueDate) return toast.error("Please select Due Date"), false;
    if (form.dueDate < form.date)
      return toast.error("Due Date must be ≥ Order Date"), false;
    if (!form.deliveryMethod.trim())
      return toast.error("Delivery Method is required"), false;
    if (!form.deliveryPlace.trim())
      return toast.error("Delivery Place is required"), false;
    if (!form.payTerms.trim()) return toast.error("Pay Terms is required"), false;
    if (!form.qtnRef.trim())
      return toast.error("Quotation Ref No is required"), false;
    if (!form.priceBasics.trim())
      return toast.error("Price Basics is required"), false;
    if (!form.warranty.trim()) return toast.error("Warranty is required"), false;
    if (!form.frtDesc.trim())
      return toast.error("Freight Description is required"), false;
    if (!form.remarks.trim()) return toast.error("Remarks is required"), false;

    for (let i = 0; i < items.length; i++) {
      if (!items[i].hsnno || items[i].hsnno === "0") {
        toast.error(`HSN Code missing for: ${items[i].fld_itemname}`);
        return false;
      }
    }
    return true;
  };

  const buildPayload = () => ({
    master: {
      Date: form.date,
      date: form.date,
      dueDate: form.dueDate,
      refDate: form.refDate || null,
      quotationdate: form.quotationDate || null,
      priorpurchaseref_no: form.refNo,
      quotationno: form.quotationNo,
      qtnref: form.qtnRef,
      supplierid: supplierId,
      branchid: branchId,
      projectid: project.id,
      employeeid: form.employee,
      frieght: freight,
      frttax: freightTax ? "1" : "0",
      frtsgst: freightTax ? frtSGST : 0,
      frtcgst: freightTax ? frtCGST : 0,
      frtigst: freightTax ? frtIGST : 0,
      insurance,
      instax: insuranceTax ? "1" : "0",
      inssgst: insuranceTax ? insSGST : 0,
      inscgst: insuranceTax ? insCGST : 0,
      insigst: insuranceTax ? insIGST : 0,
      pf,
      pftax: pfTax ? "1" : "0",
      pfsgst: pfTax ? pfSGST : 0,
      pfcgst: pfTax ? pfCGST : 0,
      pfigst: pfTax ? pfIGST : 0,
      tcsamt: tcsAmt,
      transport: totals.roundOff,
      totalvalue: totals.totalValue,
      deliverymethod: form.deliveryMethod,
      deliveryplace: form.deliveryPlace,
      payterms: form.payTerms,
      pricebasics: form.priceBasics,
      fld_warrenty: form.warranty,
      fld_frtdescription: form.frtDesc,
      remarks: form.remarks,
      userid: Number(userId) || 0,
      fld_type: branchtype_session === "HO" ? "H" : "B",
      purchaseorder_no: poNo || "",
      fld_branchwiseno: poNo || "",
      openpo: "0",
      closechecked: "0",
      cancelchecked: "0",
      ...(isEdit && poId ? { purchaseOrderId: poId, poid: poId } : {}),
    },
    details: items.map((item) => ({
      itemid: item.fld_itemid,
      ReqQty: item.fld_qty,
      POQty: item.poQty,
      ppoprice: item.price,
      PurchaseRequestid: item.fld_purchaserequestid,
      projectid: project.id,
      storeid: item.fld_storeid,
      VAT: item.sgst,
      CST: item.cgst,
      ED: item.igst,
      DS: item.dsPercent,
      SC: item.sc,
      PF: item.pandf,
      PendingQty: item.fld_pendingqty || 0,
      specification: item.specification,
      itemhsnno: item.hsnno,
    })),
  });

  const savePO = async () => {
    if (locked) return;
    if (!validate()) return;

    setSaving(true);
    try {
      const payload = buildPayload();
      const url = isEdit
        ? `${API}/updatePurchaseOrder`
        : `${API}/insertPurchaseOrder`;


// ===== DEBUG: inspect everything before insert/update =====
console.log("======= PO SAVE DEBUG =======");
console.log("mode:", isEdit ? "edit" : "add");
console.log("poId:", poId);
console.log("poNo:", poNo);
console.log("supplierId:", supplierId);
console.log("branchId:", branchId);
console.log("branchtype_session:", branchtype_session);
console.log("project:", project);
console.log("form:", form);
console.log("items:", items);
console.log("totals:", totals);
console.log("freight/insurance/pf/tcs:", {
  freight,
  freightTax,
  frtSGST,
  frtCGST,
  frtIGST,
  insurance,
  insuranceTax,
  insSGST,
  insCGST,
  insIGST,
  pf,
  pfTax,
  pfSGST,
  pfCGST,
  pfIGST,
  tcsAmt,
});
console.log("FULL PAYLOAD:", JSON.stringify(payload, null, 2));
console.log("==============================");

const res = await axios.post(
  isEdit ? `${API}/updatePurchaseOrder` : `${API}/insertPurchaseOrder`,
  payload,
  { headers: commonHeaders }
);

      if (!res.data?.success) {
        toast.error(res.data?.message || "Save failed");
        return;
      }

      const savedNo = res.data?.poNo || res.data?.purchaseOrderId || poNo;
      toast.success(
        isEdit
          ? "Purchase Order updated successfully!"
          : `Purchase Order saved${savedNo ? `: ${savedNo}` : ""}`
      );
      setTimeout(() => navigate("/inventory/PurchaseOrder_Listing"), 1200);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const gstOpts = [0, 2.5, 6, 9, 14];
  const igstOpts = [0, 5, 12, 18, 28];

  const TaxRateSelect = ({ value, onChange, opts }) => (
    <select
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      disabled={locked}
      className={`border rounded px-1.5 py-1 text-xs w-16 ${
        isDark
          ? "bg-slate-800 border-slate-600 text-white"
          : "bg-white border-slate-300"
      }`}
    >
      {opts.map((o) => (
        <option key={o} value={o}>
          {o}%
        </option>
      ))}
    </select>
  );

  const titleText = isView
    ? "View Purchase Order"
    : isEdit
    ? "Edit Purchase Order"
    : "Purchase Order Entry (GST)";

  const subtitleText = isView
    ? "Read-only purchase order details"
    : isEdit
    ? "Update purchase order with tax details"
    : "Create and save purchase order with tax details";

  return (
    <LayoutWrapper>
      <HeaderBar
        title={<span className="font-dmserif text-3xl">{titleText}</span>}
        setSkin={setSkin}
        currentSkin={skin}
      />

      <div className="space-y-6 mt-6">
        {/* Header */}
        <div
          className={`p-6 rounded-2xl shadow-xl border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `bg-white ${currentSkin.glass} text-black border-gray-200`
          }`}
        >
          <div className="flex items-center gap-3">
            <div
              className={`flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-${baseColor}-500 to-${baseColor}-600 shadow-md`}
            >
              <FileText className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">{titleText}</h1>
              <p className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                {subtitleText}
              </p>
            </div>
          </div>

          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => navigate(-1)}
              className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl border transition ${
                isDark
                  ? "border-slate-600 text-slate-300 hover:bg-slate-800"
                  : "border-slate-300 text-slate-700 hover:bg-slate-100"
              }`}
            >
              <ArrowLeft size={16} />
              Back
            </button>

            {!isView && (
              <button
                onClick={savePO}
                disabled={saving}
                className={`inline-flex items-center gap-2 px-5 py-2.5 text-white text-sm font-semibold rounded-xl shadow transition ${buttonClass} ${hoverClass} disabled:opacity-50`}
              >
                <Save size={16} />
                {saving
                  ? "Saving..."
                  : isEdit
                  ? "Update Purchase Order"
                  : "Save Purchase Order"}
              </button>
            )}
          </div>
        </div>

        {pageLoading ? (
          <div className="flex justify-center py-20">
            <span className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {/* Order Header */}
            <div
              className={`rounded-2xl shadow-xl border overflow-hidden ${
                isDark
                  ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
                  : `bg-white ${currentSkin.glass} border-gray-200`
              }`}
            >
              <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-3">
                  <div className={`w-3 h-8 rounded-full bg-${baseColor}-${baseShade}`} />
                  <Building2 size={20} className={`text-${baseColor}-600`} />
                  <h3 className="text-xl font-bold">Order Header</h3>
                </div>
              </div>

              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
                  <div className="space-y-5">
                    <div>
                      <label className={labelClass}>PO No</label>
                      <input
                        readOnly
                        value={poNo || "Auto Generated"}
                        className={roInputClass}
                      />
                    </div>
                    <div>
                      <label className={labelClass}>Ref No</label>
                      <input
                        type="text"
                        value={form.refNo}
                        onChange={(e) => setField("refNo", e.target.value)}
                        disabled={locked}
                        className={fieldClass}
                      />
                    </div>
                    <div>
                      <label className={labelClass}>
                        Date <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="date"
                        value={form.date}
                        max={todayISO()}
                        onChange={(e) => setField("date", e.target.value)}
                        disabled={locked}
                        className={fieldClass}
                      />
                    </div>
                    <div>
                      <label className={labelClass}>Quotation No</label>
                      <input
                        type="text"
                        value={form.quotationNo}
                        onChange={(e) => setField("quotationNo", e.target.value)}
                        disabled={locked}
                        className={fieldClass}
                      />
                    </div>
                    <div>
                      <label className={labelClass}>Quotation Date</label>
                      <input
                        type="date"
                        value={form.quotationDate}
                        onChange={(e) => setField("quotationDate", e.target.value)}
                        disabled={locked}
                        className={fieldClass}
                      />
                    </div>
                    <div>
                      <label className={labelClass}>
                        Qtn Ref No <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={form.qtnRef}
                        onChange={(e) => setField("qtnRef", e.target.value)}
                        disabled={locked}
                        className={fieldClass}
                      />
                    </div>
                    <div>
                      <label className={labelClass}>Project</label>
                      <input readOnly value={project.name} className={roInputClass} />
                    </div>
                  </div>

                  <div className="space-y-5">
                    <div>
                      <label className={labelClass}>Supplier</label>
                      <input readOnly value={supplierName} className={roInputClass} />
                    </div>
                    <div>
                      <label className={labelClass}>Address</label>
                      <input
                        readOnly
                        value={supplierAddress}
                        className={roInputClass}
                      />
                    </div>
                    <div>
                      <label className={labelClass}>
                        Due Date <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="date"
                        value={form.dueDate}
                        min={form.date}
                        onChange={(e) => setField("dueDate", e.target.value)}
                        disabled={locked}
                        className={fieldClass}
                      />
                    </div>
                    <div>
                      <label className={labelClass}>Ref Date</label>
                      <input
                        type="date"
                        value={form.refDate}
                        onChange={(e) => setField("refDate", e.target.value)}
                        disabled={locked}
                        className={fieldClass}
                      />
                    </div>
                    <div>
                      <label className={labelClass}>Employee</label>
                      <select
                        value={form.employee}
                        onChange={(e) => setField("employee", e.target.value)}
                        disabled={locked}
                        className={fieldClass}
                      >
                        <option value="">Select</option>
                        {project.empList.map((emp) => (
                          <option
                            key={emp.fld_employeeid}
                            value={emp.fld_employeeid}
                          >
                            {emp.fld_employee}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Items — same table as before, with disabled={locked} on inputs */}
            <div
              className={`rounded-2xl shadow-xl border overflow-hidden ${
                isDark
                  ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
                  : `bg-white ${currentSkin.glass} border-gray-200`
              }`}
            >
              <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-3">
                  <div className={`w-3 h-8 rounded-full bg-${baseColor}-${baseShade}`} />
                  <Package size={20} className={`text-${baseColor}-600`} />
                  <h3 className="text-xl font-bold">Order Items</h3>
                  <span
                    className={`px-2.5 py-1 rounded-full text-xs font-semibold ${
                      isDark ? "bg-gray-700 text-gray-300" : "bg-gray-100 text-gray-600"
                    }`}
                  >
                    {items.length} item{items.length !== 1 ? "s" : ""}
                  </span>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[1400px]">
                  <thead
                    className={`bg-gradient-to-r from-${baseColor}-600 to-${baseColor}-700 text-white`}
                  >
                    <tr>
                      {[
                        "S.No",
                        "Item Code",
                        "Item Name",
                        "HSN No",
                        "Specification",
                        "UOM",
                        "Req Qty",
                        "PO Qty",
                        "Price",
                        "Total",
                        "DS%",
                        "SGST%",
                        "CGST%",
                        "IGST%",
                      ].map((h) => (
                        <th
                          key={h}
                          className="px-4 py-3.5 text-left text-xs font-bold uppercase tracking-wider whitespace-nowrap"
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody
                    className={`divide-y ${
                      isDark ? "divide-gray-600" : "divide-gray-200"
                    }`}
                  >
                    {items.length === 0 ? (
                      <tr>
                        <td colSpan={14} className="text-center py-12 text-gray-400 text-sm">
                          No items
                        </td>
                      </tr>
                    ) : (
                      items.map((item, i) => (
                        <tr
                          key={i}
                          className={`transition ${
                            isDark
                              ? "bg-gray-800/20 hover:bg-gray-700/30"
                              : "bg-white hover:bg-gray-50"
                          }`}
                        >
                          <td className="px-4 py-3 text-center font-semibold">{i + 1}</td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span
                              className={`inline-block px-3 py-1.5 rounded-full text-sm font-semibold ${
                                isDark
                                  ? "bg-gray-700 text-gray-200"
                                  : `bg-${baseColor}-100 text-${baseColor}-800`
                              }`}
                            >
                              {item.fld_itemcode}
                            </span>
                          </td>
                          <td className="px-4 py-3 font-medium min-w-[140px]">
                            {item.fld_itemname}
                          </td>
                          <td className="px-3 py-3">
                            <input
                              type="text"
                              value={item.hsnno}
                              onChange={(e) =>
                                handleItemChange(i, "hsnno", e.target.value)
                              }
                              disabled={locked}
                              className={`${fieldClass} w-28`}
                            />
                          </td>
                          <td className="px-3 py-3">
                            <input
                              type="text"
                              value={item.specification}
                              onChange={(e) =>
                                handleItemChange(i, "specification", e.target.value)
                              }
                              disabled={locked}
                              className={`${fieldClass} w-36`}
                            />
                          </td>
                          <td className="px-4 py-3">{item.fld_uomname}</td>
                          <td className="px-4 py-3 text-center">{item.fld_qty}</td>
                          <td className="px-3 py-3">
                            <input
                              type="number"
                              value={item.poQty}
                              min={0}
                              onChange={(e) =>
                                handleItemChange(i, "poQty", e.target.value)
                              }
                              disabled={locked}
                              className={`${numInputClass} w-24`}
                            />
                          </td>
                          <td className="px-3 py-3">
                            <input
                              type="number"
                              value={item.price}
                              min={0}
                              step="0.01"
                              onChange={(e) =>
                                handleItemChange(i, "price", e.target.value)
                              }
                              disabled={locked}
                              className={`${numInputClass} w-28`}
                            />
                          </td>
                          <td className="px-4 py-3 font-bold text-right">
                            {num(item.total).toFixed(2)}
                          </td>
                          <td className="px-3 py-3">
                            <input
                              type="number"
                              value={item.dsPercent}
                              onChange={(e) =>
                                handleItemChange(i, "dsPercent", e.target.value)
                              }
                              disabled={locked}
                              className={`${numInputClass} w-20`}
                            />
                          </td>
                          <td className="px-3 py-3">
                            <input
                              type="number"
                              value={item.sgst}
                              onChange={(e) =>
                                handleItemChange(i, "sgst", e.target.value)
                              }
                              disabled={locked}
                              className={`${numInputClass} w-20`}
                            />
                          </td>
                          <td className="px-3 py-3">
                            <input
                              type="number"
                              value={item.cgst}
                              onChange={(e) =>
                                handleItemChange(i, "cgst", e.target.value)
                              }
                              disabled={locked}
                              className={`${numInputClass} w-20`}
                            />
                          </td>
                          <td className="px-3 py-3">
                            <input
                              type="number"
                              value={item.igst}
                              onChange={(e) =>
                                handleItemChange(i, "igst", e.target.value)
                              }
                              disabled={locked}
                              className={`${numInputClass} w-20`}
                            />
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Charges & Totals — same structure, disabled={locked} */}
            <div
              className={`rounded-2xl shadow-xl border overflow-hidden ${
                isDark
                  ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
                  : `bg-white ${currentSkin.glass} border-gray-200`
              }`}
            >
              <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-3">
                  <div className={`w-3 h-8 rounded-full bg-${baseColor}-${baseShade}`} />
                  <Calculator size={20} className={`text-${baseColor}-600`} />
                  <h3 className="text-xl font-bold">Charges & Totals</h3>
                </div>
              </div>

              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="space-y-5">
                    <div>
                      <label className={labelClass}>Sub Total</label>
                      <input readOnly value={totals.subtotal.toFixed(2)} className={roInputClass} />
                    </div>
                    <div>
                      <label className={labelClass}>DS</label>
                      <input readOnly value={totals.ds.toFixed(2)} className={roInputClass} />
                    </div>
                    <div>
                      <label className={labelClass}>A. Dis Total</label>
                      <input readOnly value={totals.adisTotal.toFixed(2)} className={roInputClass} />
                    </div>

                    <div>
                      <label className={labelClass}>Freight</label>
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          value={freight}
                          min={0}
                          onChange={(e) => setFreight(num(e.target.value))}
                          disabled={locked}
                          className={`${fieldClass} flex-1`}
                        />
                        <label className="flex items-center gap-1.5 text-xs whitespace-nowrap">
                          <input
                            type="checkbox"
                            checked={freightTax}
                            onChange={(e) => setFreightTax(e.target.checked)}
                            disabled={locked}
                            className="w-4 h-4 rounded"
                          />
                          Tax
                        </label>
                      </div>
                      {freightTax && (
                        <div className="flex items-center gap-2 mt-2 pl-1">
                          <span className="text-xs text-gray-500">SGST</span>
                          <TaxRateSelect value={frtSGST} onChange={setFrtSGST} opts={gstOpts} />
                          <span className="text-xs text-gray-500">CGST</span>
                          <TaxRateSelect value={frtCGST} onChange={setFrtCGST} opts={gstOpts} />
                          <span className="text-xs text-gray-500">IGST</span>
                          <TaxRateSelect value={frtIGST} onChange={setFrtIGST} opts={igstOpts} />
                        </div>
                      )}
                    </div>

                    <div>
                      <label className={labelClass}>Insurance</label>
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          value={insurance}
                          min={0}
                          onChange={(e) => setInsurance(num(e.target.value))}
                          disabled={locked}
                          className={`${fieldClass} flex-1`}
                        />
                        <label className="flex items-center gap-1.5 text-xs whitespace-nowrap">
                          <input
                            type="checkbox"
                            checked={insuranceTax}
                            onChange={(e) => setInsuranceTax(e.target.checked)}
                            disabled={locked}
                            className="w-4 h-4 rounded"
                          />
                          Tax
                        </label>
                      </div>
                      {insuranceTax && (
                        <div className="flex items-center gap-2 mt-2 pl-1">
                          <span className="text-xs text-gray-500">SGST</span>
                          <TaxRateSelect value={insSGST} onChange={setInsSGST} opts={gstOpts} />
                          <span className="text-xs text-gray-500">CGST</span>
                          <TaxRateSelect value={insCGST} onChange={setInsCGST} opts={gstOpts} />
                          <span className="text-xs text-gray-500">IGST</span>
                          <TaxRateSelect value={insIGST} onChange={setInsIGST} opts={igstOpts} />
                        </div>
                      )}
                    </div>

                    <div>
                      <label className={labelClass}>P&F</label>
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          value={pf}
                          min={0}
                          onChange={(e) => setPf(num(e.target.value))}
                          disabled={locked}
                          className={`${fieldClass} flex-1`}
                        />
                        <label className="flex items-center gap-1.5 text-xs whitespace-nowrap">
                          <input
                            type="checkbox"
                            checked={pfTax}
                            onChange={(e) => setPfTax(e.target.checked)}
                            disabled={locked}
                            className="w-4 h-4 rounded"
                          />
                          Tax
                        </label>
                      </div>
                      {pfTax && (
                        <div className="flex items-center gap-2 mt-2 pl-1">
                          <span className="text-xs text-gray-500">SGST</span>
                          <TaxRateSelect value={pfSGST} onChange={setPfSGST} opts={gstOpts} />
                          <span className="text-xs text-gray-500">CGST</span>
                          <TaxRateSelect value={pfCGST} onChange={setPfCGST} opts={gstOpts} />
                          <span className="text-xs text-gray-500">IGST</span>
                          <TaxRateSelect value={pfIGST} onChange={setPfIGST} opts={igstOpts} />
                        </div>
                      )}
                    </div>

                    <div>
                      <label className={labelClass}>
                        Frt Description <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={form.frtDesc}
                        onChange={(e) => setField("frtDesc", e.target.value)}
                        disabled={locked}
                        className={fieldClass}
                      />
                    </div>
                    <div>
                      <label className={labelClass}>
                        Remarks <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        value={form.remarks}
                        onChange={(e) => setField("remarks", e.target.value)}
                        disabled={locked}
                        className={`${fieldClass} h-24 resize-none`}
                      />
                    </div>
                    <div>
                      <label className={labelClass}>
                        Warranty <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={form.warranty}
                        onChange={(e) => setField("warranty", e.target.value)}
                        disabled={locked}
                        className={fieldClass}
                      />
                    </div>
                  </div>

                  <div className="space-y-5">
                    <div>
                      <label className={labelClass}>SGST</label>
                      <input readOnly value={totals.sgst.toFixed(2)} className={roInputClass} />
                    </div>
                    <div>
                      <label className={labelClass}>CGST</label>
                      <input readOnly value={totals.cgst.toFixed(2)} className={roInputClass} />
                    </div>
                    <div>
                      <label className={labelClass}>IGST</label>
                      <input readOnly value={totals.igst.toFixed(2)} className={roInputClass} />
                    </div>
                    <div>
                      <label className={labelClass}>Round Off</label>
                      <input readOnly value={totals.roundOff.toFixed(2)} className={roInputClass} />
                    </div>
                    <div>
                      <label className={labelClass}>
                        Delivery Method <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={form.deliveryMethod}
                        onChange={(e) => setField("deliveryMethod", e.target.value)}
                        disabled={locked}
                        className={fieldClass}
                      />
                    </div>
                    <div>
                      <label className={labelClass}>
                        Delivery Place <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={form.deliveryPlace}
                        onChange={(e) => setField("deliveryPlace", e.target.value)}
                        disabled={locked}
                        className={fieldClass}
                      />
                    </div>
                    <div>
                      <label className={labelClass}>TCS Amt</label>
                      <input
                        type="number"
                        value={tcsAmt}
                        min={0}
                        onChange={(e) => setTcsAmt(num(e.target.value))}
                        disabled={locked}
                        className={fieldClass}
                      />
                    </div>
                    <div>
                      <label className={labelClass}>
                        Pay Terms <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={form.payTerms}
                        onChange={(e) => setField("payTerms", e.target.value)}
                        disabled={locked}
                        className={fieldClass}
                      />
                    </div>
                    <div>
                      <label className={labelClass}>
                        Price Basics <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={form.priceBasics}
                        onChange={(e) => setField("priceBasics", e.target.value)}
                        disabled={locked}
                        className={fieldClass}
                      />
                    </div>

                    <div
                      className={`mt-4 p-4 rounded-xl border-2 ${
                        isDark
                          ? "border-green-700 bg-green-900/20"
                          : "border-green-300 bg-green-50"
                      }`}
                    >
                      <label
                        className={`block text-sm font-bold mb-2 ${
                          isDark ? "text-green-300" : "text-green-800"
                        }`}
                      >
                        Total Value
                      </label>
                      <input
                        readOnly
                        value={totals.totalValue}
                        className={`w-full px-3 py-3 rounded-lg border text-lg font-bold text-right ${
                          isDark
                            ? "bg-green-900/40 border-green-700 text-green-300"
                            : "bg-white border-green-400 text-green-700"
                        }`}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {!isView && (
              <div className="flex justify-end pb-4">
                <button
                  onClick={savePO}
                  disabled={saving}
                  className={`inline-flex items-center gap-2 px-8 py-3 text-white text-sm font-semibold rounded-xl shadow transition ${buttonClass} ${hoverClass} disabled:opacity-50`}
                >
                  <Save size={18} />
                  {saving
                    ? "Saving..."
                    : isEdit
                    ? "Update Purchase Order"
                    : "Save Purchase Order"}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={isDark ? "dark" : "light"}
      />
    </LayoutWrapper>
  );
};

export default PurchaseOrderEntryGst;